import React, { JSX, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { USER_LOCALES } from 'myParkdUtils/enums';
import { formatMessage } from 'i18n/CurrentLocale';
import * as languageActions from 'root/language/languageActions';
import * as urlParameterCatching from 'myParkdUtils/urlParameterCatching';
import placeholders from 'string-translations/placeholders';
import ParkdLogoWhite from 'img/parkd-logo-white.svg';
import ParkdLogoBlue from 'img/parkd-logo-blue.svg';
import { ReduxState, SupportedLocale } from 'types';
import { authSlice } from 'features/auth/authSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons';


const LogoutButton = (props: { logout: any }) => (
    <a href="# " onClick={props.logout}>
        <FormattedMessage id="GLOBAL_LOGOUT" />
    </a>
);

export const UpperNavBar = (props: Props) => {
    const [languagesDropdownIsOpen, setLanguagesDropdownIsOpen] = useState<boolean>(false);
    const refLanguageDropdown = useRef<HTMLLIElement>(null);

    const parkdLogo = props.backgroundColor === 'white'
        ? ParkdLogoBlue
        : ParkdLogoWhite;

    useEffect(() => {
        checkForURLParameters();
        const handleMouseDownEvent = (event: any) => {
            if (refLanguageDropdown.current && !refLanguageDropdown.current.contains(event.target)) {
                setLanguagesDropdownIsOpen(false)
            }
        }
        window.addEventListener('mousedown', handleMouseDownEvent);

        return () => {
            window.removeEventListener('mousedown', handleMouseDownEvent);
        };
    }, []);


    const checkForURLParameters = () => {
        const urlParameters: any = urlParameterCatching.getObjectsFromURL();
        if (urlParameters.hasOwnProperty('lang')) {
            const language = urlParameters.lang;
            switch (language) {
                case 'nl-be':
                    changeLanguage('nl-BE');
                    break;
                default:
                    changeLanguage(language);
            }
        }
    }

    const openLanguageDropdown = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setLanguagesDropdownIsOpen(true);
    };

    const changeLanguage = (language: string | SupportedLocale) => {
        const supportedLocales: SupportedLocale[] = ['en', 'de', 'fr', 'nl', 'nl-BE'];
        if (supportedLocales.includes(language as SupportedLocale)) {
            props.updateUserLanguage(language);
        } else {
            console.error(`This language parameter (${language}) is not supported in MyParkd`);
        }
        setLanguagesDropdownIsOpen(false);
    };

    return (
        <div id="upper-navbar" className={"on-top clearfix " + (props.backgroundColor === 'white' ? 'navbar-white' : 'navbar-blue')}>
            <div id="upper-left">
                <a
                    id="logo-wrap"
                    href={props.logoRedirecsTo ? props.logoRedirecsTo : '/login'}
                    target={props.openInNewTab ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                >
                    <img id="parkd-header-logo" src={parkdLogo} alt="Parkd logo" />
                </a>
                {props.telematicPartnerLogo && props.telematicPartnerLogo}
                {props.token && (
                    <Link to="/settings" id="company-name">{props.companyName}</Link>
                )}
            </div>
            <div id="upper-right">
                <nav id="main-menu">
                    {props.token ? (
                        <ul>
                            <li className="upper-link" id="logout-button">
                                <LogoutButton logout={props.logout} />
                            </li>
                        </ul>
                    ) : (
                        <div>
                            {props.showSupportPhoneNumber && (
                                <div className="show-help">
                                    <span className='t-20-500'><FormattedMessage id='REGISTRATION_NEED_HELP' /></span>&nbsp;
                                    <span>
                                        <FontAwesomeIcon icon={faPhone} size='xs' color='#8694A0' />&nbsp;
                                        <FormattedMessage id='GLOBAL_SUPPORT_PHONE_NUMBER' />
                                    </span>
                                </div>
                            )}

                            <ul className='inline-block'>
                                <li className="upper-link" id="language-switcher" ref={refLanguageDropdown}>
                                    <a id="current-language" href="#" onClick={openLanguageDropdown}>
                                        {formatMessage(placeholders[props.userLanguage])}
                                    </a>
                                    {languagesDropdownIsOpen && (
                                        <ul id="dropdown-languages">
                                            {USER_LOCALES.map((language, index) => (
                                                <li key={index}>
                                                    <span onClick={() => changeLanguage(language)}>
                                                        {formatMessage(placeholders[language])}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            </ul>
                        </div>
                    )}
                </nav>
            </div>
        </div>
    );
}


type OwnProps = {
    logoRedirecsTo?: string,
    openInNewTab?: boolean
    backgroundColor?: 'white' | 'blue',
    showSupportPhoneNumber?: boolean,
    telematicPartnerLogo?: JSX.Element
};
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps & OwnProps;


function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth.token,
        companyName: state.companyDetails.companyDetails.name,
        companyCountryCode: state.companyDetails.companyDetails.address.countryCode,
        userLanguage: state.userLanguage.userLanguage,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        logout: authSlice.actions.logout,
        updateUserLanguage: languageActions.updateUserLanguage,
    }, dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(UpperNavBar);